import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Canonical one-lesson-per-learner-local-day ENTRY gate.
 *
 * The learner is told before any lesson work begins — not after the quiz.
 * `can_start_lesson` is server-authoritative (learner-validated IANA
 * timezone, DST-safe, privileged QA bypass only) and a lesson that was
 * legitimately started stays finishable across the midnight boundary.
 */
export interface LessonStartGateState {
  loading: boolean;
  allowed: boolean;
  reason: string | null;
  completedLessonName: string | null;
  nextAvailableAt: string | null;
}

const INITIAL: LessonStartGateState = {
  loading: true,
  allowed: false,
  reason: null,
  completedLessonName: null,
  nextAvailableAt: null,
};

export function useLessonStartGate(lessonId: string | undefined | null): LessonStartGateState {
  const [state, setState] = useState<LessonStartGateState>(INITIAL);

  useEffect(() => {
    let cancelled = false;
    if (!lessonId) {
      setState({ ...INITIAL, loading: false });
      return;
    }
    setState(INITIAL);
    (async () => {
      const { data, error } = await (supabase as never as {
        rpc: (n: string, a: unknown) => Promise<{ data: unknown; error: { message: string } | null }>;
      }).rpc("can_start_lesson", { p_lesson_id: lessonId });
      if (cancelled) return;
      if (error) {
        // Availability problem must not lock a paying learner out of a lesson
        // they are entitled to: the server trigger still enforces the cap.
        setState({ ...INITIAL, loading: false, allowed: true, reason: "gate_unavailable" });
        return;
      }
      const row = (data ?? {}) as {
        allowed?: boolean;
        reason?: string;
        completed_lesson_name?: string;
        next_lesson_available_at?: string;
      };
      setState({
        loading: false,
        allowed: Boolean(row.allowed),
        reason: row.reason ?? null,
        completedLessonName: row.completed_lesson_name ?? null,
        nextAvailableAt: row.next_lesson_available_at ?? null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [lessonId]);

  return state;
}
